import React from 'react';
import SportsIcon from '@mui/icons-material/Sports';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import { GiBullseye, GiSoccerKick, GiShield, GiGoalKeeper, GiRunningShoe, GiSoccerBall } from 'react-icons/gi';
import { FaPercentage, FaArrowUp, FaUsers, FaArrowCircleUp, FaFistRaised } from 'react-icons/fa';
import { MdLeaderboard, MdOutlineHandshake, MdCelebration } from 'react-icons/md';
import { RiMentalHealthFill } from 'react-icons/ri';
import { TbTrophy } from 'react-icons/tb';

// Player achievements from MatchReport.js organized by category
export const PLAYER_ACHIEVEMENT_CATEGORIES = {
    'Performance-Based': [
        { id: 'potm', title: 'Managers Player of the Match', description: 'Awarded by the manager or coach', icon: <StarIcon className="achievement-icon" sx={{ fontSize: 60, color: '#f1c40f' }} /> },
        { id: 'goal-machine', title: 'Goal Machine', description: 'Scored the most goals', icon: <GiBullseye className="achievement-icon" style={{ fontSize: 60, color: '#e74c3c' }} /> },
        { id: 'playmaker', title: 'Playmaker', description: 'Created the most chances or assists', icon: <GiSoccerKick className="achievement-icon" style={{ fontSize: 60, color: '#3498db' }} /> },
        { id: 'defensive-rock', title: 'Defensive Rock', description: 'Best at stopping attacks or making clearances', icon: <GiShield className="achievement-icon" style={{ fontSize: 60, color: '#7f8c8d' }} /> },
        { id: 'safe-hands', title: 'Safe Hands', description: 'Best saves or goalkeeping effort', icon: <GiGoalKeeper className="achievement-icon" style={{ fontSize: 60, color: '#f39c12' }} /> },
        { id: 'tireless-engine', title: 'Tireless Engine', description: 'Most distance covered or never stopped running', icon: <GiRunningShoe className="achievement-icon" style={{ fontSize: 60, color: '#2ecc71' }} /> },
        { id: 'skill-star', title: 'Skill Star', description: 'Showed great dribbling or technique', icon: <GiSoccerBall className="achievement-icon" style={{ fontSize: 60, color: '#9b59b6' }} /> }
    ],
    'Effort and Attitude': [
        { id: '100-percent', title: '100% Effort', description: 'Gave full commitment throughout the match', icon: <FaPercentage className="achievement-icon" style={{ fontSize: 60, color: '#27ae60' }} /> },
        { id: 'most-improved', title: 'Most Improved', description: 'Made noticeable progress from last game', icon: <FaArrowUp className="achievement-icon" style={{ fontSize: 60, color: '#9b59b6' }} /> },
        { id: 'team-first', title: 'Team First', description: 'Helped others, supported teammates', icon: <FaUsers className="achievement-icon" style={{ fontSize: 60, color: '#3498db' }} /> },
        { id: 'bounceback', title: 'Bounceback Award', description: 'Overcame a mistake and kept going', icon: <FaArrowCircleUp className="achievement-icon" style={{ fontSize: 60, color: '#e67e22' }} /> },
        { id: 'never-gave-up', title: 'Never Gave Up', description: 'Kept trying even when it was tough', icon: <FaFistRaised className="achievement-icon" style={{ fontSize: 60, color: '#c0392b' }} /> }
    ],
    'Leadership & Behaviour': [
        { id: 'captains-mindset', title: 'Captain\'s Mindset', description: 'Led by example on and off the ball', icon: <MdLeaderboard className="achievement-icon" style={{ fontSize: 60, color: '#2c3e50' }} /> },
        { id: 'respect-award', title: 'Respect Award', description: 'Showed great sportsmanship', icon: <MdOutlineHandshake className="achievement-icon" style={{ fontSize: 60, color: '#3498db' }} /> },
        { id: 'positive-voice', title: 'Positive Voice', description: 'Encouraged others throughout the game', icon: <RiMentalHealthFill className="achievement-icon" style={{ fontSize: 60, color: '#2ecc71' }} /> }
    ],
    'Fun & Spirit': [
        { id: 'magic-moment', title: 'Magic Moment', description: 'One standout play or special moment', icon: <MdCelebration className="achievement-icon" style={{ fontSize: 60, color: '#f1c40f' }} /> },
        { id: 'cool-pressure', title: 'Cool Under Pressure', description: 'Stayed calm in key moments', icon: <TbTrophy className="achievement-icon" style={{ fontSize: 60, color: '#3498db' }} /> },
        { id: 'first-touch', title: 'First Touch Wizard', description: 'Showed great ball control', icon: <GiSoccerBall className="achievement-icon" style={{ fontSize: 60, color: '#9b59b6' }} /> }
    ]
};

// Original achievements
export const achievements = [
    {
        id: 'iron_man',
        title: 'Iron Man',
        description: 'Attend 10 consecutive games',
        icon: <SportsIcon sx={{ fontSize: 80 }} />
    },
    {
        id: 'hat_trick',
        title: 'Hat-Trick Hero',
        description: 'Score three goals in a single game',
        icon: <SportsSoccerIcon sx={{ fontSize: 80 }} />
    },
    {
        id: 'mvp',
        title: 'MVP',
        description: 'Be voted Player of the Match three times',
        icon: <EmojiEventsIcon sx={{ fontSize: 80 }} />
    },
    {
        id: 'hot_streak',
        title: 'Hot Streak',
        description: 'Win 5 games in a row',
        icon: <WhatshotIcon sx={{ fontSize: 80 }} />
    },
    {
        id: 'star_performer',
        title: 'Parents Player of the Match',
        description: 'Voted Player of the Match by parents',
        icon: <StarIcon sx={{ fontSize: 80 }} />
    },
    {
        id: 'potm',
        title: 'Managers Player of the Match',
        description: 'Awarded by the manager or coach',
        icon: <EmojiEventsIcon sx={{ fontSize: 80, color: '#f1c40f' }} />
    },
    {
        id: 'first_strike',
        title: 'First Strike',
        description: 'First Goal Scored',
        icon: <MilitaryTechIcon sx={{ fontSize: 80 }} />
    },
    ...Object.values(PLAYER_ACHIEVEMENT_CATEGORIES).flat().filter(award => award.id !== 'potm')
];

export const getMatchReportAchievementGroups = () => (
    Object.fromEntries(
        Object.entries(PLAYER_ACHIEVEMENT_CATEGORIES).map(([category, awards]) => [
            category,
            awards.map(award => ({
                id: award.id,
                label: `${award.title} – ${award.description}`
            }))
        ])
    )
);

export const getAchievementTitleById = (achievementId) => {
    for (const awards of Object.values(PLAYER_ACHIEVEMENT_CATEGORIES)) {
        const found = awards.find(award => award.id === achievementId);
        if (found) {
            return found.title;
        }
    }
    const found = achievements.find(award => award.id === achievementId);
    return found ? found.title : '';
};

export const AwardTitle = ({ title }) => {
    const match = String(title || '').match(/^(Parents|Managers)(\s+.*)$/);
    if (!match) {
        return title;
    }

    return (
        <>
            <span className={`award-role award-role--${match[1].toLowerCase()}`}>{match[1]}</span>
            <span className="award-rest">{match[2].trim()}</span>
        </>
    );
}; 